import { consentLogger } from './consentLogger';
import { withWindow } from './withWindow';

/**
 * Polaris consent categories used in data-polaris-consent attributes
 */
export type ConsentCategory = 'essential' | 'statistics' | 'marketing';

/**
 * Consent state for all categories
 */
export type ConsentState = Readonly<{
  essential: boolean;
  statistics: boolean;
  marketing: boolean;
}>;

/**
 * Polaris cookie name prefixes
 * Polaris consent cookies can have various prefixes depending on the version
 */
const POLARIS_COOKIE_PREFIXES = ['polaris_consent=', '_polaris_consent=', 'truevault_polaris='] as const;

/**
 * Parse the Polaris consent cookie from document.cookie
 *
 * Splits document.cookie, finds the Polaris consent cookie by known prefixes,
 * decodes and JSON-parses it once.
 *
 * @returns The parsed consent cookie object, or null on failure
 */
function parsePolarisConsentCookie(): Record<string, unknown> | null {
  const cookies = document.cookie.split(';').map((c) => c.trim());

  const polarisConsentCookie = cookies.find((c) => POLARIS_COOKIE_PREFIXES.some((prefix) => c.startsWith(prefix)));

  if (!polarisConsentCookie) {
    return null;
  }

  try {
    const cookieValue = polarisConsentCookie.substring(polarisConsentCookie.indexOf('=') + 1);
    const decoded = decodeURIComponent(cookieValue);

    try {
      return JSON.parse(decoded);
    } catch {
      consentLogger.warn('Consent cookie is not valid JSON:', decoded);
      return null;
    }
  } catch (e) {
    consentLogger.warn('Failed to parse consent cookie:', e);
    return null;
  }
}

/**
 * Get consent state for all categories
 *
 * Parses the consent cookie once and builds the full state from the single result.
 *
 * @returns The current consent state for all categories
 */
export function getConsentState(): ConsentState {
  return withWindow(
    () => {
      const consentData = parsePolarisConsentCookie();

      return {
        essential: true,
        statistics: consentData ? Boolean(consentData['statistics']) : false,
        marketing: consentData ? Boolean(consentData['marketing']) : false,
      };
    },
    { essential: true, statistics: false, marketing: false }
  );
}

/**
 * Wait for Polaris consent to be determined, then call callback with consent state
 *
 * This function listens for the GPP useractioncomplete event to detect when the user
 * has made their consent choices. It also checks if consent has already been determined.
 *
 * The callback is guaranteed to be invoked exactly once, even if consent is already
 * determined when this function is called.
 *
 * @param callback - Function to call when consent is ready, receives the consent state
 * @returns Cleanup function to remove event listeners (call when component unmounts)
 *
 * @example
 * ```typescript
 * const cleanup = onConsentReady((consent) => {
 *   console.log('Consent state:', consent);
 *   initializeAnalytics(consent);
 * });
 *
 * // Later, when cleaning up:
 * cleanup();
 * ```
 */
export function onConsentReady(callback: (consent: ConsentState) => void): () => void {
  return withWindow(
    (global) => {
      // Guard flag to ensure callback only fires once
      let hasCalledBack = false;
      let eventListenerCallback: ((event: { eventName: string }) => void) | undefined;

      const safeCallback = (consent: ConsentState) => {
        if (hasCalledBack) return;
        hasCalledBack = true;
        callback(consent);
      };

      if (global.__gpp) {
        // Check if consent already determined
        global.__gpp('ping', (pingData) => {
          consentLogger.log('Checking consent state:', pingData);

          if (pingData.cmpStatus === 'loaded' && pingData.signalStatus === 'ready') {
            // Consent already determined
            const consent = getConsentState();
            consentLogger.log('Consent already determined:', consent);
            safeCallback(consent);
          }
        });

        // Also listen for new consent events
        eventListenerCallback = (event) => {
          if (event.eventName === 'useractioncomplete') {
            const consent = getConsentState();
            consentLogger.log('User action complete, consent state:', consent);
            safeCallback(consent);
          }
        };

        global.__gpp('addEventListener', eventListenerCallback, 'useractioncomplete');

        // Return cleanup function
        return () => {
          if (global.__gpp && eventListenerCallback) {
            global.__gpp('removeEventListener', eventListenerCallback, 'useractioncomplete');
          }
        };
      } else {
        consentLogger.warn('GPP API not available');
        // Fallback: assume no consent
        safeCallback({
          essential: true,
          statistics: false,
          marketing: false,
        });
        return () => {}; // No cleanup needed
      }
    },
    () => {}
  );
}
